import axios from "axios";

function mapBrasilApiToViaCep(data = {}) {
  return {
    cep: data.cep || "",
    logradouro: data.street || "",
    complemento: "",
    unidade: "",
    bairro: data.neighborhood || "",
    localidade: data.city || "",
    uf: data.state || "",
    erro: !data.cep
  };
}

function buildErrorResponse(source, error, fallback = {}) {
  const status = error?.response?.status;
  const payload = error?.response?.data || {};

  return {
    data: {
      erro: true,
      source,
      status,
      message: payload.message || error?.message || "Erro ao consultar CEP",
      name: payload.name || error?.name,
      type: payload.type || "cep_lookup_error",
      ...fallback
    }
  };
}

export async function getAddressByCEP(cep) {
  const sanitizedCEP = String(cep).replace(/\D/g, "");

  try {
    const response = await axios.get(
      `https://brasilapi.com.br/api/cep/v2/${sanitizedCEP}`
    );

    if (!response?.data?.cep) {
      throw new Error("BrasilAPI returned invalid payload");
    }

    return {
      ...response,
      data: mapBrasilApiToViaCep(response.data)
    };
  } catch (brasilApiError) {
    const brasilApiStatus = brasilApiError?.response?.status;

    try {
      const viaCepResponse = await axios.get(
        `https://viacep.com.br/ws/${sanitizedCEP}/json/`
      );

      if (!viaCepResponse?.data || viaCepResponse.data.erro) {
        return buildErrorResponse("viacep", viaCepResponse);
      }

      // Mantém compatibilidade e sinaliza que houve erro na API primária.
      return {
        ...viaCepResponse,
        data: {
          ...viaCepResponse.data,
          brasilApiFallback: true
        }
      };
    } catch (viaCepError) {
      return buildErrorResponse("viacep", viaCepError, {
        brasilApiError: {
          status: brasilApiStatus,
          message:
            brasilApiError?.response?.data?.message || brasilApiError?.message
        }
      });
    }
  }
}

// Cache em memória por CEP, para o contexto de entrega.
//
// A implementação acima dispara uma consulta a cada CEP completo digitado. Com o campo do
// painel isso vira uma rajada de requisições a serviços externos enquanto o cliente ainda
// está corrigindo o número. O debounce da digitação pertence ao campo; a memoização, aqui.
//
// Só resultados bem-sucedidos entram no cache: CEP inexistente e falha de rede precisam
// poder ser tentados de novo. A relação entre CEP e logradouro é estável, então o cache não
// expira dentro da sessão.
const cepCache = new Map();

export async function getAddressByCEPCached(cep) {
  const sanitizedCEP = String(cep || "").replace(/\D/g, "");

  if (sanitizedCEP.length !== 8) {
    return { data: { erro: true, type: "cep_lookup_error" } };
  }

  if (cepCache.has(sanitizedCEP)) {
    return { data: cepCache.get(sanitizedCEP) };
  }

  const response = await getAddressByCEP(sanitizedCEP);

  if (response?.data && !response.data.erro) {
    cepCache.set(sanitizedCEP, response.data);
  }

  return response;
}
